import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Tag as TagIcon, RefreshCw } from "lucide-react";

type EcardTag = {
  id: string;
  name: string;
  color: string;
  applies_to_card: boolean;
  applies_to_promotion: boolean;
};

type Topup = {
  id: string;
  ecard_id: string;
  topup_date: string;
  topup_amount: number;
  bonus_amount: number;
  apply_amount: number;
  promo_id: string | null;
};

type ECard = {
  id: string;
  card_last4: string;
  card_number: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  status: string;
  sold_by_driver_name: string | null;
  purchase_date: string | null;
};

type Promo = {
  id: string;
  promo_name: string;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    Number.isFinite(n) ? n : 0
  );

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const escape = (v: string | number) => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ECardTagReport() {
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<EcardTag[]>([]);
  const [topups, setTopups] = useState<Topup[]>([]);
  const [cards, setCards] = useState<ECard[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [topupTagLinks, setTopupTagLinks] = useState<Array<{ topup_id: string; tag_id: string }>>([]);
  const [cardTagLinks, setCardTagLinks] = useState<Array<{ ecard_id: string; tag_id: string }>>([]);
  const [promoTagLinks, setPromoTagLinks] = useState<Array<{ promo_id: string; tag_id: string }>>([]);

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [tagFilter, setTagFilter] = useState<string>("all");

  async function loadAll() {
    setLoading(true);
    try {
      const [tRes, tpRes, cRes, pRes, ttRes, ctRes, ptRes] = await Promise.all([
        supabase.from("ecard_tags").select("id, name, color, applies_to_card, applies_to_promotion").order("name"),
        supabase.from("ecard_topups").select("id, ecard_id, topup_date, topup_amount, bonus_amount, apply_amount, promo_id"),
        supabase.from("ecards").select("id, card_last4, card_number, customer_first_name, customer_last_name, status, sold_by_driver_name, purchase_date"),
        supabase.from("ecard_promotions").select("id, promo_name"),
        supabase.from("ecard_topup_tags").select("topup_id, tag_id"),
        supabase.from("ecard_card_tags").select("ecard_id, tag_id"),
        supabase.from("ecard_promo_tags").select("promo_id, tag_id"),
      ]);
      if (tRes.error) throw tRes.error;
      if (tpRes.error) throw tpRes.error;
      if (cRes.error) throw cRes.error;
      if (pRes.error) throw pRes.error;

      setTags((tRes.data ?? []) as EcardTag[]);
      setTopups((tpRes.data ?? []) as Topup[]);
      setCards((cRes.data ?? []) as ECard[]);
      setPromos((pRes.data ?? []) as Promo[]);
      setTopupTagLinks((ttRes.data ?? []) as Array<{ topup_id: string; tag_id: string }>);
      setCardTagLinks((ctRes.data ?? []) as Array<{ ecard_id: string; tag_id: string }>);
      setPromoTagLinks((ptRes.data ?? []) as Array<{ promo_id: string; tag_id: string }>);
    } catch (e: any) {
      toast({ title: "Failed to load tag report", description: e?.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  // Build lookup maps
  const cardById = useMemo(() => {
    const m = new Map<string, ECard>();
    for (const c of cards) m.set(c.id, c);
    return m;
  }, [cards]);

  const promoById = useMemo(() => {
    const m = new Map<string, Promo>();
    for (const p of promos) m.set(p.id, p);
    return m;
  }, [promos]);

  const topupTagsByTopupId = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const r of topupTagLinks) {
      if (!m[r.topup_id]) m[r.topup_id] = [];
      m[r.topup_id].push(r.tag_id);
    }
    return m;
  }, [topupTagLinks]);

  const cardTagsByCardId = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const r of cardTagLinks) {
      if (!m[r.ecard_id]) m[r.ecard_id] = [];
      m[r.ecard_id].push(r.tag_id);
    }
    return m;
  }, [cardTagLinks]);

  const tagById = useMemo(() => {
    const m = new Map<string, EcardTag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  // Filter top-ups by date range
  const filteredTopups = useMemo(() => {
    return topups.filter((t) => {
      const d = (t.topup_date ?? "").slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }, [topups, dateFrom, dateTo]);

  // Per-tag aggregates
  const tagSummary = useMemo(() => {
    type Agg = {
      tagId: string;
      tagName: string;
      tagColor: string;
      cardCount: number;
      topupCount: number;
      totalPaid: number;
      totalBonus: number;
    };
    const rows: Agg[] = [];
    for (const t of tags) {
      const cardIds = new Set((cardTagLinks.filter((l) => l.tag_id === t.id)).map((l) => l.ecard_id));
      const topupIdsForTag = new Set(topupTagLinks.filter((l) => l.tag_id === t.id).map((l) => l.topup_id));

      let topupCount = 0;
      let totalPaid = 0;
      let totalBonus = 0;
      for (const tp of filteredTopups) {
        if (topupIdsForTag.has(tp.id)) {
          topupCount += 1;
          totalPaid += Number(tp.topup_amount) || 0;
          totalBonus += Number(tp.bonus_amount) || 0;
        }
      }

      rows.push({
        tagId: t.id,
        tagName: t.name,
        tagColor: t.color,
        cardCount: cardIds.size,
        topupCount,
        totalPaid,
        totalBonus,
      });
    }
    return rows
      .filter((r) => tagFilter === "all" || r.tagId === tagFilter)
      .sort((a, b) => b.totalPaid - a.totalPaid);
  }, [tags, cardTagLinks, topupTagLinks, filteredTopups, tagFilter]);

  // Per-topup detail rows (used for CSV export and drill-down)
  const detailRows = useMemo(() => {
    const out: Array<{
      topup_id: string;
      topup_date: string;
      ecard_id: string;
      card_last4: string;
      card_number: string;
      customer_name: string;
      sold_by_driver_name: string;
      purchase_date: string;
      promo_name: string;
      topup_amount: number;
      bonus_amount: number;
      apply_amount: number;
      tag_names: string;
      card_tag_names: string;
    }> = [];

    for (const tp of filteredTopups) {
      const card = cardById.get(tp.ecard_id);
      const promo = tp.promo_id ? promoById.get(tp.promo_id) : null;
      const tagIds = topupTagsByTopupId[tp.id] ?? [];
      const cardTagIds = cardTagsByCardId[tp.ecard_id] ?? [];

      // If tag filter is active, only include rows where this tag is on the topup OR the card
      if (tagFilter !== "all") {
        const hasOnTopup = tagIds.includes(tagFilter);
        const hasOnCard = cardTagIds.includes(tagFilter);
        if (!hasOnTopup && !hasOnCard) continue;
      }

      const tagNames = tagIds.map((id) => tagById.get(id)?.name).filter(Boolean).join("; ");
      const cardTagNames = cardTagIds.map((id) => tagById.get(id)?.name).filter(Boolean).join("; ");
      const customerName = card
        ? `${card.customer_first_name ?? ""} ${card.customer_last_name ?? ""}`.trim()
        : "";

      out.push({
        topup_id: tp.id,
        topup_date: (tp.topup_date ?? "").slice(0, 10),
        ecard_id: tp.ecard_id,
        card_last4: card?.card_last4 ?? "",
        card_number: card?.card_number ?? "",
        customer_name: customerName,
        sold_by_driver_name: card?.sold_by_driver_name ?? "",
        purchase_date: card?.purchase_date ?? "",
        promo_name: promo?.promo_name ?? "",
        topup_amount: Number(tp.topup_amount) || 0,
        bonus_amount: Number(tp.bonus_amount) || 0,
        apply_amount: Number(tp.apply_amount) || 0,
        tag_names: tagNames,
        card_tag_names: cardTagNames,
      });
    }
    return out.sort((a, b) => (a.topup_date < b.topup_date ? 1 : -1));
  }, [filteredTopups, cardById, promoById, topupTagsByTopupId, cardTagsByCardId, tagById, tagFilter]);

  function exportSummaryCSV() {
    const headers = ["Tag", "Cards Tagged", "Top-ups", "Total Paid", "Total Bonus"];
    const rows = tagSummary.map((r) => [
      r.tagName,
      r.cardCount,
      r.topupCount,
      r.totalPaid.toFixed(2),
      r.totalBonus.toFixed(2),
    ]);
    const range = dateFrom || dateTo ? `_${dateFrom || "all"}_to_${dateTo || "all"}` : "_all-dates";
    downloadCSV(`ecard-tag-summary${range}.csv`, headers, rows);
    toast({ title: "Summary CSV exported", description: `${rows.length} rows` });
  }

  function exportDetailCSV() {
    const headers = [
      "Topup Date",
      "Card Last4",
      "Card Number",
      "Customer",
      "Sold By Driver",
      "Card Purchase Date",
      "Promotion",
      "Topup Amount",
      "Bonus Amount",
      "Apply Amount",
      "Topup Tags",
      "Card Tags",
    ];
    const rows = detailRows.map((r) => [
      r.topup_date,
      r.card_last4,
      r.card_number,
      r.customer_name,
      r.sold_by_driver_name,
      r.purchase_date,
      r.promo_name,
      r.topup_amount.toFixed(2),
      r.bonus_amount.toFixed(2),
      r.apply_amount.toFixed(2),
      r.tag_names,
      r.card_tag_names,
    ]);
    const range = dateFrom || dateTo ? `_${dateFrom || "all"}_to_${dateTo || "all"}` : "_all-dates";
    const tagSuffix = tagFilter !== "all" ? `_${tagById.get(tagFilter)?.name ?? ""}` : "";
    downloadCSV(`ecard-tag-detail${range}${tagSuffix}.csv`, headers, rows);
    toast({ title: "Detail CSV exported", description: `${rows.length} rows` });
  }

  function exportCardsCSV() {
    // Card-level export (everything tagged on cards)
    const headers = [
      "Card Last4",
      "Card Number",
      "Customer",
      "Status",
      "Sold By Driver",
      "Purchase Date",
      "Card Tags",
      "Total Top-ups (in range)",
      "Total Paid (in range)",
      "Total Bonus (in range)",
    ];

    const filteredTopupsByCard: Record<string, Topup[]> = {};
    for (const tp of filteredTopups) {
      if (!filteredTopupsByCard[tp.ecard_id]) filteredTopupsByCard[tp.ecard_id] = [];
      filteredTopupsByCard[tp.ecard_id].push(tp);
    }

    const rows = cards
      .filter((c) => {
        if (tagFilter === "all") return true;
        const tagIds = cardTagsByCardId[c.id] ?? [];
        return tagIds.includes(tagFilter);
      })
      .map((c) => {
        const tagIds = cardTagsByCardId[c.id] ?? [];
        const tagNames = tagIds.map((id) => tagById.get(id)?.name).filter(Boolean).join("; ");
        const cardTopups = filteredTopupsByCard[c.id] ?? [];
        const tCount = cardTopups.length;
        const tPaid = cardTopups.reduce((s, t) => s + (Number(t.topup_amount) || 0), 0);
        const tBonus = cardTopups.reduce((s, t) => s + (Number(t.bonus_amount) || 0), 0);
        const customerName = `${c.customer_first_name ?? ""} ${c.customer_last_name ?? ""}`.trim();
        return [
          c.card_last4,
          c.card_number,
          customerName,
          c.status,
          c.sold_by_driver_name ?? "",
          c.purchase_date ?? "",
          tagNames,
          tCount,
          tPaid.toFixed(2),
          tBonus.toFixed(2),
        ];
      });

    const range = dateFrom || dateTo ? `_${dateFrom || "all"}_to_${dateTo || "all"}` : "_all-dates";
    downloadCSV(`ecard-cards-by-tag${range}.csv`, headers, rows);
    toast({ title: "Cards CSV exported", description: `${rows.length} rows` });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TagIcon className="h-5 w-5" />
            Tag Reporting
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>From Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <Label>Tag Filter</Label>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {tags.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setTagFilter("all"); }}>
                Clear
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(isoDate(new Date(Date.now() - 30 * 86400000))); setDateTo(isoDate(new Date())); }}>
                Last 30d
              </Button>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Date range filters by top-up date. Leave blank to include all dates. CSV exports respect the active filters.
          </div>
        </CardContent>
      </Card>

      {/* Per-tag summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Per-Tag Summary</CardTitle>
          <Button variant="outline" size="sm" onClick={exportSummaryCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export Summary CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tag</TableHead>
                  <TableHead className="text-right">Cards Tagged</TableHead>
                  <TableHead className="text-right">Top-ups</TableHead>
                  <TableHead className="text-right">Total Paid</TableHead>
                  <TableHead className="text-right">Total Bonus</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tagSummary.map((r) => (
                  <TableRow key={r.tagId}>
                    <TableCell>
                      <Badge style={{ backgroundColor: r.tagColor, color: "#fff" }}>{r.tagName}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{r.cardCount}</TableCell>
                    <TableCell className="text-right">{r.topupCount}</TableCell>
                    <TableCell className="text-right text-blue-600 font-medium">{fmtMoney(r.totalPaid)}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">{fmtMoney(r.totalBonus)}</TableCell>
                  </TableRow>
                ))}
                {tagSummary.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      No tags with data in selected range.
                    </TableCell>
                  </TableRow>
                )}
                {tagSummary.length > 0 && (
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{tagSummary.reduce((s, r) => s + r.cardCount, 0)}</TableCell>
                    <TableCell className="text-right">{tagSummary.reduce((s, r) => s + r.topupCount, 0)}</TableCell>
                    <TableCell className="text-right text-blue-600">{fmtMoney(tagSummary.reduce((s, r) => s + r.totalPaid, 0))}</TableCell>
                    <TableCell className="text-right text-green-600">{fmtMoney(tagSummary.reduce((s, r) => s + r.totalBonus, 0))}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail rows */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Top-up Detail ({detailRows.length} rows)</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCardsCSV}>
              <Download className="h-4 w-4 mr-1" />
              Export Cards CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportDetailCSV}>
              <Download className="h-4 w-4 mr-1" />
              Export Detail CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md max-h-[500px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Promo</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Bonus</TableHead>
                  <TableHead>Tags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailRows.slice(0, 500).map((r) => (
                  <TableRow key={r.topup_id}>
                    <TableCell className="text-sm">{formatDate(r.topup_date)}</TableCell>
                    <TableCell className="text-sm font-medium">****{r.card_last4}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm">{r.promo_name || "—"}</TableCell>
                    <TableCell className="text-sm text-right">{fmtMoney(r.topup_amount)}</TableCell>
                    <TableCell className="text-sm text-right">{fmtMoney(r.bonus_amount)}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap gap-1">
                        {r.tag_names.split("; ").filter(Boolean).map((n) => {
                          const tag = tags.find((t) => t.name === n);
                          return (
                            <Badge key={n} style={{ backgroundColor: tag?.color ?? "#3b82f6", color: "#fff" }} className="text-[10px]">
                              {n}
                            </Badge>
                          );
                        })}
                        {r.card_tag_names.split("; ").filter(Boolean).map((n) => {
                          const tag = tags.find((t) => t.name === n);
                          return (
                            <Badge key={`c-${n}`} variant="outline" style={{ borderColor: tag?.color ?? "#3b82f6", color: tag?.color ?? "#3b82f6" }} className="text-[10px]">
                              {n}
                            </Badge>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {detailRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                      No top-ups match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {detailRows.length > 500 && (
            <div className="text-xs text-muted-foreground mt-2">
              Showing first 500 rows. Export CSV to see all {detailRows.length} rows.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
